/**
 * Display-only mirror of backend/functions/fees.js. The payout a seller
 * actually receives is computed server-side on delivery confirmation; this
 * exists so the pricing page and listing screens can quote the same numbers.
 * Keep the two in sync.
 */

// Highest band first; the first band whose `min` the unit price reaches wins.
export const FEE_TIERS = [
  { min: 1000, max: Infinity, rate: 0.02, label: "$1,000.00+" },
  { min: 500, max: 999.99, rate: 0.03, label: "$500.00 – $999.99" },
  { min: 250, max: 499.99, rate: 0.04, label: "$250.00 – $499.99" },
  { min: 100, max: 249.99, rate: 0.05, label: "$100.00 – $249.99" },
  { min: 50, max: 99.99, rate: 0.06, label: "$50.00 – $99.99" },
  { min: 10, max: 49.99, rate: 0.07, label: "$10.00 – $49.99" },
  { min: 0, max: 9.99, rate: 0.08, label: "$0.01 – $9.99" },
];

export const PLAN_FEE_RULES = {
  // Pro keeps the same ladder but never pays more than 5%, so every band
  // below $250 collapses to 5%. Elite ignores the ladder entirely.
  // `incomeHoldDays` is how long delivered sale income stays out of the
  // withdrawable balance. The faster plans buy a shorter hold, never none:
  // card funds need about two business days to reach the platform, and the
  // hold is also the window in which a fraudulent sale can still be caught.
  basic: { maxRate: 0.08, incomeHoldDays: 7 },
  pro: { maxRate: 0.05, incomeHoldDays: 3 },
  elite: { flatRate: 0.02, incomeHoldDays: 3 },
};

// Applied to a plan carrying no hold of its own. The safe default is the
// longest, never zero.
export const DEFAULT_INCOME_HOLD_DAYS = 7;

const DEFAULT_PLAN = "basic";

/**
 * 'proYearly' and 'pro' are the same plan for fee purposes, and a 'free'
 * (or missing) membership falls back to the Basic ladder.
 */
export const normalizePlan = (membership) => {
  const plan = String(membership || "").replace(/Yearly$/i, "").toLowerCase();
  return PLAN_FEE_RULES[plan] ? plan : DEFAULT_PLAN;
};

/** The fee rate for one unit at `unitPrice` under `membership`, as a fraction. */
export const getFeeRate = (unitPrice, membership) => {
  const rules = PLAN_FEE_RULES[normalizePlan(membership)];

  if (rules.flatRate !== undefined) {
    return rules.flatRate;
  }

  const price = Number(unitPrice) || 0;
  const tier =
    FEE_TIERS.find((candidate) => price >= candidate.min) ||
    FEE_TIERS[FEE_TIERS.length - 1];

  return Math.min(tier.rate, rules.maxRate);
};

/** The effective ladder for a plan, with the plan's cap or flat rate applied. */
export const feeTiersForPlan = (membership) =>
  FEE_TIERS.map((tier) => ({
    ...tier,
    rate: getFeeRate(tier.min, membership),
  }));

export const formatFeeRate = (rate) => `${(rate * 100).toFixed(0)}%`;

/** What the seller keeps on a single item priced at `unitPrice`. */
export const estimateSellerPayout = (unitPrice, membership) => {
  const price = Number(unitPrice) || 0;
  const rate = getFeeRate(price, membership);
  const fee = Math.round(price * rate * 100) / 100;

  return { rate, fee, payout: Math.round((price - fee) * 100) / 100 };
};

/**
 * How many days this plan withholds delivered sale income before it can be
 * withdrawn. Never returns 0.
 */
export const getIncomeHoldDays = (membership) => {
  const days = PLAN_FEE_RULES[normalizePlan(membership)].incomeHoldDays;
  return Number.isFinite(days) && days > 0 ? days : DEFAULT_INCOME_HOLD_DAYS;
};
